<!--
  This work has been created or enhanced by
    Markus Luckey, 2025, KERNBLICK GmbH

  This file is covered by the EU PL 1.2 license.
  You may obtain a copy of the licence at
  https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
-->

<template>
  <nav class="navbar navbar-expand-lg shadow">
    <div class="container-fluid">
      <router-link to="/">
        <img
          id="logo"
          alt="CIVITAS/CORE Platform"
          src="/img/civitascore.png"
          width="60"
          class="me-5"
        >
      </router-link>
      <button
        class="navbar-toggler"
        type="button"
      >
        <div
          class="hamburger-toggle"
          onclick="this.children[0].classList.toggle('active'); document.getElementById('navbar-content').classList.toggle('show');"
        >
          <div class="hamburger">
            <span />
            <span />
            <span />
          </div>
        </div>
      </button>
      <div
        id="navbar-content"
        class="collapse navbar-collapse"
      >
        <ul class="navbar-nav mr-auto mb-2 mb-lg-0 flex-wrap ms-auto">
          <li
            v-for="(app, index) in filteredApps"
            :key="index"
            class="nav-item mx-2 d-flex align-items-center"
          >
            <router-link
              :to="app.path"
              class="nav-link"
            >
              <BPopover
                :click="false"
                :close-on-hide="true"
                :delay="{show: 0, hide: 0}"
              >
                <template #target>
                  <div class="d-flex align-items-center">
                    <img
                      v-if="app.icon"
                      :src="app.icon"
                      class="icon me-1"
                      :style="{ backgroundColor: app.color || 'none' }"
                      width="28"
                    >
                    <span class="title no-wrap">{{ app.name }}</span>
                  </div>
                </template>
                <template #title>
                  {{ app.name || 'no title' }}
                </template>
                <template #default>
                  {{ app.description[$i18n.locale] || Object.values(app.description)[0] || 'no description' }}
                </template>
              </BPopover>
            </router-link>
          </li>
          <li
            class="nav-item"
          >
            <a
              class="nav-link"
              href="https://gitlab.com/civitas-connect/civitas-core/civitas-core/-/issues"
              target="_blank"
            >
              <img
                src="/img/gitlab2.png"
                class="icon me-1"
                width="24"
              >
              <span class="title">{{ $t('repository') }}</span>
            </a>
          </li>
          <li
            class="nav-item"
          >
            <a
              class="nav-link"
              href="https://docs.core.civitasconnect.digital/"
              target="_blank"
            >
              <img
                src="/img/civitascore_square.png"
                class="icon me-1"
                width="28"
              >
              <span class="title">{{ $t('docs') }}</span>
            </a>
          </li>
          <li
            v-show="false"
            class="nav-item"
          >
            <router-link
              v-show="signedIn"
              class="nav-link disabled"
              to="/logout"
            >
              <img
                src="https://icongr.am/fontawesome/sign-out.svg?size=28&color=ffffff"
                class="icon"
              >
              <span class="title">{{ $t('signOut') }}</span>
            </router-link>
          </li>
          <li
            class="nav-item"
          >
            <button
              class="btn btn-link nav-link mx-1"
              @click="toggleColorMode"
            >
              <font-awesome-icon :icon="isDarkMode ? 'moon' : 'sun'" />
            </button>
          </li>
        </ul>
      </div>
    </div>
  </nav>
</template>

<script>
import Mgr from '@/services/SecurityService';
import { BPopover } from 'bootstrap-vue-next';
import { reactive } from 'vue';

import {useColorMode} from 'bootstrap-vue-next'

export default {
  name: 'HamMenu',
  components: {
    BPopover,
  },
  props: {
    apps: { type: Array, default: () => [] }
  },
  data() {
    return {
      mgr: new Mgr(),
      menu: false,
      signedIn: false,
      roles: reactive([]),
      isDarkMode: localStorage.getItem('colorMode') === 'dark',
    }
  },
  computed: {
    isIsignedIn() {
      return this.signedIn;
    },
    filteredApps() {
      return this.apps.filter((app) => {
        return (!app.role || this.roles.includes(app.role)) && app.iframe && !app.hide;
      });
    }
  },
  async mounted() {

    if (this.mgr.isAuthorizationEnabled()) {
      await this.checkSignedIn();
      this.roles = (await this.mgr.getRoles()) || [];
    } else {
      this.roles = [];
    }

    const colorMode = useColorMode(); // Get the reactive color mode reference
    if (localStorage.getItem('colorMode') === 'dark') {
      colorMode.value = 'dark';
    } else {
      colorMode.value = 'light';
    }
  },
  methods: {
    async checkSignedIn() {
      if (this.mgr.isAuthorizationEnabled()) {
        this.signedIn = await this.mgr.getSignedIn();
      } else {
        this.signedIn = true;
      }
    },
    async toggleColorMode() {
      const colorMode = useColorMode(); // Get the reactive color mode reference
      this.isDarkMode = !this.isDarkMode; // Toggle the mode
      colorMode.value = this.isDarkMode ? 'dark' : 'light'; // Update the color mode
      localStorage.setItem('colorMode', this.isDarkMode ? 'dark' : 'light');
    },
  },
};
</script>

<style>


@keyframes fadeIn {
	 from {
		 opacity: 0;
	}
	 to {
		 opacity: 1;
	}
}

.navbar-toggler {
	 border: none;
	 padding: 0;
	 outline: none;
}
.navbar-toggler:focus {
	 box-shadow: none;
}
.navbar-toggler .hamburger-toggle {
	 position: relative;
	 display: inline-block;
	 width: 50px;
	 height: 50px;
	 z-index: 11;
}
.navbar-toggler .hamburger-toggle .hamburger {
	 position: absolute;
	 transform: translate(-50%, -50%) rotate(0deg);
	 left: 50%;
	 top: 50%;
	 width: 50%;
	 height: 50%;
	 pointer-events: none;
}
.navbar-toggler .hamburger-toggle .hamburger span {
	 width: 100%;
	 height: 4px;
	 position: absolute;
	 background: rgba(var(--bs-emphasis-color-rgb), 0.7);
	 border-radius: 2px;
	 z-index: 1;
	 transition: transform 0.2s cubic-bezier(0.77, 0.2, 0.05, 1), background 0.2s cubic-bezier(0.77, 0.2, 0.05, 1), all 0.2s ease-in-out;
	 left: 0px;
}
.navbar-toggler .hamburger-toggle .hamburger span:first-child {
	 top: 10%;
	 transform-origin: 50% 50%;
	 transform: translate(0% -50%) !important;
}
.navbar-toggler .hamburger-toggle .hamburger span:nth-child(2) {
	 top: 50%;
	 transform: translate(0, -50%);
}
.navbar-toggler .hamburger-toggle .hamburger span:last-child {
	 left: 0px;
	 top: auto;
	 bottom: 10%;
	 transform-origin: 50% 50%;
}
.navbar-toggler .hamburger-toggle .hamburger.active span {
	 position: absolute;
	 margin: 0;
}
.navbar-toggler .hamburger-toggle .hamburger.active span:first-child {
	 top: 45%;
	 transform: rotate(45deg);
}
.navbar-toggler .hamburger-toggle .hamburger.active span:nth-child(2) {
	 left: 50%;
	 width: 0px;
}
.navbar-toggler .hamburger-toggle .hamburger.active span:last-child {
	 top: 45%;
	 transform: rotate(-45deg);
}
</style>
